// Generated from scripts/analytics-events.ts. Rebuild with pnpm scripts:build.
import { isProductHandle, isProductId } from '@theme/product-id';
import { CollectionUpdateEvent, SearchUpdateEvent, StandardEvents } from '@shopify/events';
/*
 * The analytics owner: the one place where the theme tells Shopify's pixels about interactions that Shopify's own
 * standard events do not cover. The modules that own those interactions publish DOM events and know nothing about
 * analytics; this module listens to them at `document` and publishes each as a custom event,
 * `Shopify.analytics.publish('wontio:<name>', payload)`. The catalog of events and payload fields is
 * `ANALYTICS_EVENTS`, and `.agents/skills/wontio-integrations/references/boundary-contract.md` lists the same
 * names and fields; `pnpm check` compares the two.
 *
 * - Once per action. Every source event is published once, as it happens. A filter or search update settles later (the
 *   count of results is part of the event), so it is published when its promise resolves; one that is superseded or
 *   fails publishes nothing, and when several updates of one element settle together only the newest is published,
 *   because only its results were ever on screen.
 * - No personal data. A payload holds product ids and handles, counts, the names of theme surfaces and the names of
 *   filters. The search term never enters one (its length does), nor does anything a shopper typed or chose in a
 *   filter. Every value that comes from an event is checked against a pattern first.
 * - Shopify's consent path is the only exit. Delivery goes through `Shopify.analytics.publish` and nothing else: this
 *   module makes no request, writes no storage, posts no message and fills no data layer. Shopify's pixel manager
 *   decides which pixels receive an event, and pixels operate on the consent that the visitor gave.
 * - Absence is normal. Without `Shopify.analytics` (no pixel manager, a blocked script, a fixture) nothing is published
 *   and nothing is reported; a failing call is absorbed. Nothing is published in the theme editor.
 * - Standard events stay Shopify's: the theme never publishes one, and publishes no event for an interaction that a
 *   standard event already reports. The standard `shopify:search:update` and `shopify:collection:update` events are DOM
 *   events for functional reactions and say so (they fire whatever the visitor's consent); they are read here only to
 *   learn about the instant search results and the filter and sort changes that no pixel event reports.
 *
 * The module has no element and no teardown: its listeners belong to the document and are added once.
 */
/** The prefix of every published event; Shopify asks custom events to carry a unique one. */
const NAMESPACE = 'wontio';
/**
 * The events this module publishes and the fields of each payload, in the order the catalog lists them. A field that
 * ends in `?` is optional. The type of a payload is derived from this list, so a payload can neither miss a required
 * field nor carry one that is not listed.
 */
export const ANALYTICS_EVENTS = {
    quick_view_opened: ['product_id', 'product_handle?', 'surface'],
    search_performed: ['term_length', 'results_count'],
    filter_applied: ['context', 'collection_handle?', 'filter_count', 'filter_names', 'sort_key?', 'results_count'],
    wishlist_changed: ['reason', 'product_id?', 'product_handle?', 'list_size', 'added?', 'refused?'],
    compare_changed: ['reason', 'product_id?', 'product_handle?', 'pick_count'],
    popup_shown: ['section_id', 'popup_type'],
    popup_dismissed: ['section_id', 'popup_type'],
    popup_converted: ['section_id', 'popup_type'],
    bundle_added: ['product_id?', 'kind', 'item_count'],
    bottom_navigation_used: ['item'],
    linked_product_selected: ['product_id', 'from_product_id', 'product_handle?', 'surface'],
};
/** The DOM events of the owning modules that this module listens to; each is the `eventName` of a class in `scripts/`. */
export const DOM_EVENTS = {
    quickViewOpen: 'quick-view:open',
    wishlistChange: 'wishlist:change',
    compareChange: 'compare:change',
    popupShow: 'popup:show',
    popupDismiss: 'popup:dismiss',
    popupConvert: 'popup:convert',
    bundleAdd: 'bundle:add',
    bottomNavigationUse: 'bottom-navigation:use',
    linkedProductSelect: 'linked-products:select',
};
/* ----- values that come from events ----- */
function isRecord(value) {
    return typeof value === 'object' && value !== null;
}
/** The detail of a custom event, or undefined when the event is not one or its detail is not an object. */
function detailOf(event) {
    return event instanceof CustomEvent && isRecord(event.detail) ? event.detail : undefined;
}
function idOf(value) {
    return isProductId(value) ? value : undefined;
}
function handleOf(value) {
    return isProductHandle(value) ? value : undefined;
}
/** The handle in a product address, such as `/products/handle?variant=1`. */
function handleOfUrl(value) {
    if (typeof value !== 'string')
        return undefined;
    try {
        const segment = /\/products\/([^/?#]+)/.exec(new URL(value, window.location.href).pathname)?.[1];
        return segment === undefined ? undefined : handleOf(decodeURIComponent(segment));
    }
    catch {
        return undefined;
    }
}
/** A count, or undefined when the value is not a whole number of zero or more. */
function countOf(value) {
    return typeof value === 'number' && Number.isSafeInteger(value) && value >= 0 ? value : undefined;
}
/** The name of a theme surface or a kind: a short lowercase token, never free text. */
function tokenOf(value) {
    return typeof value === 'string' && /^[a-z][a-z0-9_-]{0,31}$/.test(value) ? value : undefined;
}
/** The id of a theme section, which Liquid builds from the template and the section's own name. */
function sectionIdOf(value) {
    return typeof value === 'string' && /^[\w-]{1,100}$/.test(value) ? value : undefined;
}
function oneOf(value, allowed) {
    return allowed.find((candidate) => candidate === value);
}
/* ----- the way out ----- */
function publish(name, payload) {
    // The theme editor shows the merchant's own clicks: they say nothing about shoppers.
    if (typeof Shopify === 'undefined' || Shopify.designMode === true)
        return;
    const analytics = Shopify.analytics;
    if (typeof analytics?.publish !== 'function')
        return;
    const data = {};
    for (const [field, value] of Object.entries(payload))
        if (value !== undefined)
            data[field] = value;
    try {
        // Whether the call answers with a promise is Shopify's to decide; a rejection is absorbed either way.
        void Promise.resolve(analytics.publish(`${NAMESPACE}:${name}`, data)).catch(() => undefined);
    }
    catch {
        // The pixel layer belongs to Shopify: a failure in it is not the shopper's or the page's to see.
    }
}
/* ----- events published once, as they happen ----- */
function onQuickViewOpen(event) {
    const detail = detailOf(event);
    const productId = idOf(detail?.productId);
    if (!detail || !productId)
        return;
    publish('quick_view_opened', {
        product_id: productId,
        product_handle: handleOfUrl(detail.productUrl),
        surface: tokenOf(detail.context) ?? 'other',
    });
}
const WISHLIST_REASONS = ['add', 'remove', 'merge', 'full'];
function onWishlistChange(event) {
    const detail = detailOf(event);
    // `sync` is the list changing without the shopper: another tab, a restored page or a product that no longer exists.
    const reason = oneOf(detail?.reason, WISHLIST_REASONS);
    if (!detail || !reason || !Array.isArray(detail.items))
        return;
    const item = isRecord(detail.item) ? detail.item : undefined;
    publish('wishlist_changed', {
        reason,
        product_id: idOf(item?.id),
        product_handle: handleOf(item?.handle),
        list_size: detail.items.length,
        added: countOf(detail.added),
        refused: countOf(detail.refused),
    });
}
const COMPARE_REASONS = ['add', 'remove', 'clear', 'full'];
function onCompareChange(event) {
    const detail = detailOf(event);
    const reason = oneOf(detail?.reason, COMPARE_REASONS);
    if (!detail || !reason || !Array.isArray(detail.picks))
        return;
    const pick = isRecord(detail.pick) ? detail.pick : undefined;
    publish('compare_changed', {
        reason,
        product_id: idOf(pick?.id),
        product_handle: handleOf(pick?.handle),
        pick_count: detail.picks.length,
    });
}
/** The payload that the three popup events share. */
function popupPayload(event) {
    const detail = detailOf(event);
    const sectionId = sectionIdOf(detail?.sectionId);
    const popupType = tokenOf(detail?.type);
    return sectionId && popupType ? { section_id: sectionId, popup_type: popupType } : undefined;
}
function onPopupShow(event) {
    const payload = popupPayload(event);
    if (payload)
        publish('popup_shown', payload);
}
function onPopupDismiss(event) {
    const payload = popupPayload(event);
    if (payload)
        publish('popup_dismissed', payload);
}
function onPopupConvert(event) {
    const payload = popupPayload(event);
    if (payload)
        publish('popup_converted', payload);
}
const BUNDLE_KINDS = ['bundle', 'accessories'];
function onBundleAdd(event) {
    const detail = detailOf(event);
    const kind = oneOf(detail?.kind, BUNDLE_KINDS);
    const itemCount = countOf(detail?.itemCount);
    if (!detail || !kind || itemCount === undefined)
        return;
    publish('bundle_added', { product_id: idOf(detail.productId), kind, item_count: itemCount });
}
function onBottomNavigationUse(event) {
    const item = tokenOf(detailOf(event)?.item);
    if (item)
        publish('bottom_navigation_used', { item });
}
const LINKED_PRODUCT_SURFACES = ['product', 'card'];
function onLinkedProductSelect(event) {
    const detail = detailOf(event);
    const productId = idOf(detail?.productId);
    const fromProductId = idOf(detail?.fromProductId);
    const surface = oneOf(detail?.surface, LINKED_PRODUCT_SURFACES);
    if (!detail || !productId || !fromProductId || !surface)
        return;
    publish('linked_product_selected', {
        product_id: productId,
        from_product_id: fromProductId,
        product_handle: handleOf(detail.productHandle),
        surface,
    });
}
/* ----- events published once their results are known ----- */
/** The newest update each element has announced, so a burst of changes is reported once, by its last settled update. */
const newestUpdate = new WeakMap();
function whenSettled(event, report) {
    const { target } = event;
    if (target)
        newestUpdate.set(target, event);
    event.promise.then((result) => {
        if (target) {
            if (newestUpdate.get(target) !== event)
                return;
            newestUpdate.delete(target);
        }
        report(result);
    }, () => {
        if (target && newestUpdate.get(target) === event)
            newestUpdate.delete(target);
    });
}
function surfaceOf(event) {
    return event.detail?.surface;
}
const MAX_FILTER_NAMES = 20;
const MAX_FILTER_NAME_LENGTH = 64;
/**
 * What filters were applied, by name only. A filter of Shopify's standard events is one object with one key: the kind
 * (`price`, `available`, `productVendor`, `variantOption` and so on) with its value. The name is the kind, and for the
 * kinds that a merchant defines, the option or metafield too. The values are left out: a price range is what a shopper
 * typed or dragged, and the other values are the catalog's own words, which the names and the count do not need.
 */
function filterNames(filters) {
    const names = new Set();
    for (const filter of filters ?? []) {
        const kind = Object.keys(filter)[0];
        if (kind === undefined)
            continue;
        const value = filter[kind];
        let qualifier;
        if (isRecord(value)) {
            if (typeof value.name === 'string')
                qualifier = value.name;
            else if (typeof value.namespace === 'string' && typeof value.key === 'string')
                qualifier = `${value.namespace}.${value.key}`;
            else if (typeof value.key === 'string')
                qualifier = value.key;
        }
        names.add(qualifier === undefined ? kind : `${kind}:${qualifier}`.slice(0, MAX_FILTER_NAME_LENGTH));
    }
    return [...names].sort().slice(0, MAX_FILTER_NAMES);
}
function sortKeyOf(value) {
    return typeof value === 'string' && /^[A-Z_]{1,32}$/.test(value) ? value : undefined;
}
function publishFilterApplied(update) {
    const results = countOf(update.results);
    if (results === undefined)
        return;
    publish('filter_applied', {
        context: update.context,
        collection_handle: update.collectionHandle,
        filter_count: update.filters?.length ?? 0,
        filter_names: filterNames(update.filters),
        sort_key: sortKeyOf(update.sortKey),
        results_count: results,
    });
}
function onSearchUpdate(event) {
    if (!(event instanceof SearchUpdateEvent))
        return;
    const surface = surfaceOf(event);
    if (surface === 'predictive') {
        // The length is all that is kept of what the shopper typed.
        const termLength = [...event.search.query.trim()].length;
        if (termLength === 0)
            return;
        whenSettled(event, ({ totalCount }) => {
            const results = countOf(totalCount);
            if (results !== undefined)
                publish('search_performed', { term_length: termLength, results_count: results });
        });
    }
    else if (surface === 'facets') {
        const { productFilters, sortKey } = event.search;
        whenSettled(event, ({ totalCount }) => publishFilterApplied({ context: 'search', filters: productFilters, sortKey, results: totalCount }));
    }
}
function onCollectionUpdate(event) {
    if (!(event instanceof CollectionUpdateEvent) || surfaceOf(event) !== 'facets')
        return;
    const { collection, productFilters, sortKey } = event;
    whenSettled(event, ({ productsCount }) => publishFilterApplied({
        context: 'collection',
        collectionHandle: handleOf(collection.handle),
        filters: productFilters,
        sortKey,
        results: productsCount,
    }));
}
document.addEventListener(DOM_EVENTS.quickViewOpen, onQuickViewOpen);
document.addEventListener(DOM_EVENTS.wishlistChange, onWishlistChange);
document.addEventListener(DOM_EVENTS.compareChange, onCompareChange);
document.addEventListener(DOM_EVENTS.popupShow, onPopupShow);
document.addEventListener(DOM_EVENTS.popupDismiss, onPopupDismiss);
document.addEventListener(DOM_EVENTS.popupConvert, onPopupConvert);
document.addEventListener(DOM_EVENTS.bundleAdd, onBundleAdd);
document.addEventListener(DOM_EVENTS.bottomNavigationUse, onBottomNavigationUse);
document.addEventListener(DOM_EVENTS.linkedProductSelect, onLinkedProductSelect);
document.addEventListener(StandardEvents.searchUpdate, onSearchUpdate);
document.addEventListener(StandardEvents.collectionUpdate, onCollectionUpdate);
